import { Box, FormControl, FormLabel, Typography } from '@mui/material';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { CabButton, CabDropdown, CabModal, CabTextInput } from '@CabComponents';
import { Meeting } from '../../../store';

type ReminderDuration = 'days' | 'weeks' | 'months';

const durationOptions = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
];

const getInitialDuration = (reminderAt?: string | null): { amount: number; duration: ReminderDuration } => {
  if (!reminderAt) return { amount: 1, duration: 'weeks' };
  const days = Math.max(1, Math.ceil(DateTime.fromISO(reminderAt).diffNow('days').days));
  return days % 7 === 0
    ? { amount: days / 7, duration: 'weeks' }
    : { amount: days, duration: 'days' };
};

const getReminderDate = (amount: number, duration: ReminderDuration): DateTime => {
  if (duration === 'months') return DateTime.now().plus({ months: amount });
  if (duration === 'weeks') return DateTime.now().plus({ weeks: amount });
  return DateTime.now().plus({ days: amount });
};

interface FollowUpReminderModalProps {
  meeting: Meeting | null;
  onClose: () => void;
  onSave: (meeting: Meeting, reminderAt: string | null) => Promise<void>;
}

export const FollowUpReminderModal = ({ meeting, onClose, onSave }: FollowUpReminderModalProps): ReactElement => {
  const [amount, setAmount] = useState(1);
  const [duration, setDuration] = useState<ReminderDuration>('weeks');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const initial = getInitialDuration(meeting?.follow_up_reminder_at);
    setAmount(initial.amount);
    setDuration(initial.duration);
  }, [meeting]);

  const reminderDate = useMemo(() => getReminderDate(amount, duration), [amount, duration]);

  const save = async (reminderAt: string | null) => {
    if (!meeting) return;
    setIsSaving(true);
    try {
      await onSave(meeting, reminderAt);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <CabModal
      open={meeting !== null}
      onClose={onClose}
      title="Follow-up reminder"
      closeIcon
      actionButtons={(
        <>
          {meeting?.follow_up_reminder_at && (
            <CabButton buttonType="tertiary" color="error" onClick={() => save(null)} disabled={isSaving}>
              Remove reminder
            </CabButton>
          )}
          <CabButton buttonType="secondary" onClick={onClose} disabled={isSaving}>Cancel</CabButton>
          <CabButton onClick={() => save(reminderDate.toUTC().toISO())} disabled={amount <= 0 || isSaving}>
            {isSaving ? 'Saving...' : 'Save reminder'}
          </CabButton>
        </>
      )}
      sx={{ '& .MuiDialog-paper': { width: 500 } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, paddingTop: 2 }}>
        <Typography color="text.secondary">
          We’ll notify you if this meeting has not been booked or voted on after this amount of time.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl sx={{ flex: 1 }}>
            <FormLabel>Number</FormLabel>
            <CabTextInput
              type="number"
              value={amount}
              onChange={(event) => setAmount(Math.max(1, Number(event.target.value) || 1))}
              inputProps={{ min: 1 }}
            />
          </FormControl>
          <FormControl sx={{ flex: 1 }}>
            <FormLabel>Duration</FormLabel>
            <CabDropdown
              value={duration}
              onChange={(event) => setDuration(event.target.value as ReminderDuration)}
              options={durationOptions}
            />
          </FormControl>
        </Box>
        <Box sx={{ padding: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Reminder date:</strong>&nbsp;
            {reminderDate.toLocaleString(DateTime.DATE_MED)}
          </Typography>
        </Box>
      </Box>
    </CabModal>
  );
};
